import { useMemo, useRef, useLayoutEffect } from 'react';
import { useT } from '@/i18n/LanguageContext';

const DAY = 30;             // px per day
const LABEL_W = 200;        // left label column
const BAR_H = 26;           // bar height
const TRACK_H = 32;         // vertical space per stacked track

const STATUS_COLOR = {
  planned: '#a1a1aa', released: '#3b82f6', in_progress: '#f59e0b',
  completed: '#10b981', on_hold: '#f43f5e', cancelled: '#d4d4d8',
};

const toDate = (s) => {
  if (!s) return null;
  const d = new Date(String(s).slice(0, 10) + 'T00:00:00');
  return isNaN(d) ? null : d;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const diffDays = (a, b) => Math.round((a - b) / 86400000);

// Greedy interval packing: assign each order to the first track whose last bar ends before it starts.
function packTracks(orders) {
  const sorted = [...orders].sort((a, b) => a.s - b.s);
  const trackEnds = []; // end date per track
  const placed = [];
  for (const o of sorted) {
    let track = trackEnds.findIndex((end) => end < o.s);
    if (track === -1) { track = trackEnds.length; trackEnds.push(o.e); }
    else trackEnds[track] = o.e;
    placed.push({ ...o, track });
  }
  return { placed, tracks: Math.max(1, trackEnds.length) };
}

// Production schedule Gantt. lanes: work centers [{id, code, name}]. orders: work orders
// [{id, wo_no, product_name, status, scheduled_start, scheduled_end, ...}]. Rows = lanes,
// bars = orders stacked so they never overlap. An 未割当 lane collects orders with no lane.
export default function ProductionGantt({ lanes = [], orders = [], onOrderClick, emptyText }) {
  const { t } = useT();
  const scrollRef = useRef(null);

  const mapped = useMemo(() => orders.map((o) => {
    let s = toDate(o.scheduled_start), e = toDate(o.scheduled_end);
    if (!s && e) s = e;
    if (!e && s) e = s;
    return { ...o, s, e };
  }).filter((o) => o.s && o.e), [orders]);

  const laneRows = useMemo(() => {
    const laneList = [...lanes];
    const laneIds = new Set(laneList.map((l) => l.id));
    const rows = laneList.map((l) => ({ id: l.id, label: l.name || l.code, sub: l.code, orders: [] }));
    const byId = Object.fromEntries(rows.map((r) => [r.id, r]));
    const unassigned = { id: '__none__', label: t('mfg.sched.unassigned'), sub: '', orders: [] };
    mapped.forEach((o) => {
      if (o.work_center_id && laneIds.has(o.work_center_id)) byId[o.work_center_id].orders.push(o);
      else unassigned.orders.push(o);
    });
    const all = [...rows];
    if (unassigned.orders.length) all.push(unassigned);
    return all.map((r) => ({ ...r, ...packTracks(r.orders) }));
  }, [lanes, mapped, t]);

  const bounds = useMemo(() => {
    if (mapped.length === 0) return null;
    const all = mapped.flatMap((o) => [o.s, o.e]);
    return { min: addDays(new Date(Math.min(...all)), -2), max: addDays(new Date(Math.max(...all)), 3) };
  }, [mapped]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !bounds) return;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (today < bounds.min || today > bounds.max) return;
    const todayLeft = LABEL_W + diffDays(today, bounds.min) * DAY + DAY / 2;
    el.scrollLeft = Math.max(0, todayLeft - el.clientWidth / 2);
  }, [bounds]);

  if (mapped.length === 0) {
    return <div className="py-16 text-center text-sm text-zinc-400" data-testid="prod-gantt-empty">{emptyText || t('mfg.sched.empty')}</div>;
  }

  const { min, max } = bounds;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const totalDays = diffDays(max, min) + 1;
  const width = totalDays * DAY;
  const days = Array.from({ length: totalDays }, (_, i) => addDays(min, i));
  const months = [];
  days.forEach((d, i) => {
    const key = `${d.getFullYear()}/${d.getMonth() + 1}`;
    const last = months[months.length - 1];
    if (!last || last.key !== key) months.push({ key, start: i, count: 1 });
    else last.count++;
  });
  const todayLeft = today >= min && today <= max ? diffDays(today, min) * DAY + DAY / 2 : null;

  return (
    <div ref={scrollRef} className="overflow-x-auto rounded-xl border border-zinc-200 bg-white" data-testid="prod-gantt">
      <div style={{ minWidth: LABEL_W + width }}>
        {/* header */}
        <div className="flex sticky top-0 z-20 bg-zinc-50 border-b border-zinc-200">
          <div className="shrink-0 sticky left-0 z-30 bg-zinc-50 border-r border-zinc-200 px-3 py-2 text-xs font-semibold text-zinc-500" style={{ width: LABEL_W }}>{t('mfg.sched.workCenter')}</div>
          <div className="relative" style={{ width }}>
            <div className="flex">
              {months.map((m) => (
                <div key={m.key} style={{ width: m.count * DAY }} className="border-r border-zinc-200 px-2 py-1 text-[11px] font-semibold text-zinc-600">{m.key}</div>
              ))}
            </div>
            <div className="flex border-t border-zinc-200">
              {days.map((d) => (
                <div key={d.getTime()} style={{ width: DAY }} className={`text-center text-[10px] py-0.5 ${d.getDay() === 0 || d.getDay() === 6 ? 'bg-zinc-100/70 text-zinc-400' : 'text-zinc-400'}`}>{d.getDate()}</div>
              ))}
            </div>
          </div>
        </div>

        {/* lanes */}
        {laneRows.map((lane) => {
          const laneH = lane.tracks * TRACK_H + 8;
          return (
            <div key={lane.id} className="flex border-b border-zinc-200 hover:bg-zinc-50/40" data-testid={`prod-lane-${lane.id}`}>
              <div className="shrink-0 sticky left-0 z-10 bg-white border-r border-zinc-200 px-3 py-2 flex flex-col justify-center" style={{ width: LABEL_W, minHeight: laneH }}>
                <p className="text-sm font-medium text-zinc-800 truncate">{lane.label}</p>
                {lane.sub && <p className="text-[11px] font-mono text-zinc-400 truncate">{lane.sub}</p>}
              </div>
              <div className="relative" style={{ width, height: laneH }}>
                {todayLeft != null && <div className="absolute top-0 bottom-0 w-px bg-rose-400/70 z-0" style={{ left: todayLeft }} />}
                {lane.placed.map((o) => {
                  const left = diffDays(o.s, min) * DAY;
                  const w = (diffDays(o.e, o.s) + 1) * DAY;
                  return (
                    <button key={o.id} type="button" onClick={() => onOrderClick?.(o)} title={`${o.wo_no} — ${o.product_name}`} data-testid={`prod-bar-${o.id}`}
                      className="absolute rounded-md shadow-sm text-[11px] text-white px-2 truncate flex items-center gap-1 hover:brightness-110 transition-all z-[5]"
                      style={{ left, width: Math.max(w, DAY), top: o.track * TRACK_H + 4, height: BAR_H, backgroundColor: STATUS_COLOR[o.status] || '#6366f1' }}>
                      <span className="font-semibold">{o.manufacturing_no || o.wo_no}</span>
                      <span className="opacity-90 truncate"><span data-no-translate>{o.product_name}</span></span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { STATUS_COLOR };
